import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGlobalFontSize } from '../context/GlobalFontSizeContext';
import { useUser } from '../context/UserContext';
import { useBiqPageContext } from '../context/BiqPageContext';
import { getUsage, getTokenPackages, buyExtraTokens } from '../utils/api';
import { purchaseTokenPackage } from '../services/revenuecatService';
import { USAGE_FEATURES, getUsageLimit } from '../constants/UsageLimits';
import { getUsageDisplay, normalizeFeatureUsage } from '../utils/usageDisplay';
import { ROUTES } from '../constants/Routes';
import { formatTierName } from '../utils/formatTierName';
import PageSkeleton from '../components/PageSkeleton';
import BuyExtraPackageModal, { EXTRA_PACKAGES } from '../components/BuyExtraPackageModal';

/** Display order and API keys from POST /get_usage */
const USAGE_PAGE_FEATURES = [
  { apiKey: 'biq', legacyKey: USAGE_FEATURES.BIQ_QUERY, nameKey: 'usage.features.biq' },
  { apiKey: 'deep_thinking', legacyKey: USAGE_FEATURES.BIQ_DEEP_THINKING, nameKey: 'usage.features.deepThinking' },
  { apiKey: 'analytics', legacyKey: USAGE_FEATURES.ANALYTICS_VIEW, nameKey: 'usage.features.analytics' },
  { apiKey: 'smart_alerts', legacyKey: USAGE_FEATURES.DEFAULT_ALERT_CREATE, nameKey: 'usage.features.smartAlerts' },
  { apiKey: 'custom_alerts', legacyKey: USAGE_FEATURES.CUSTOM_ALERT_CREATE, nameKey: 'usage.features.customAlerts' },
];

/**
 * Rows are grouped in threes: BiQ + Deep thinking, then Analytics views,
 * then Smart alerts + Custom alerts. A divider (24px above and below) closes
 * each group; rows inside a group are separated by a plain 32px gap.
 */
const hasSeparatorAfter = (index) => index === 1 || index === 2;

/** Shared card chrome so both cards on the page stay identical. */
const CARD_CLASS =
  'bg-white dark:bg-[#1d1d1d] border border-[#E8EBE9] dark:border-[#30363d] rounded-[12px]';

const Usage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { fontSize } = useGlobalFontSize();
  const { email, tier, tierLimits, globalTierLimits } = useUser();
  const { setPageExtras } = useBiqPageContext() || {};
  const [loading, setLoading] = useState(true);
  const [usageFeatures, setUsageFeatures] = useState(null);
  const [resetInDays, setResetInDays] = useState(null);
  const [error, setError] = useState(null);
  const [buyExtraModalType, setBuyExtraModalType] = useState(null);
  const [packages, setPackages] = useState(null);

  const loadUsage = useCallback(async ({ silent = false } = {}) => {
    if (!email || !tier) return;

    try {
      if (!silent) setLoading(true);
      setError(null);

      const response = await getUsage();

      if (response.status && response.features) {
        setUsageFeatures(response.features);
        if (typeof response.reset_in_days === 'number') {
          setResetInDays(response.reset_in_days);
        }
      } else {
        setError('Invalid response from server');
      }
    } catch (err) {
      console.error('Failed to fetch usage:', err);
      setError(err.message || 'Failed to load usage information');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [email, tier]);

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await getTokenPackages();
        if (response.status && response.packages) {
          setPackages(response.packages);
        }
      } catch (err) {
        console.error('Failed to fetch token packages:', err);
      }
    };
    fetchPackages();
  }, []);

  const getFeatureUsage = useCallback((apiKey, legacyKey) => {
    const fallbackLimit = getUsageLimit(tier, legacyKey, tierLimits, globalTierLimits);
    const fallbackNumeric =
      fallbackLimit === Infinity ? -1 : fallbackLimit;
    const raw = usageFeatures?.[apiKey];
    return normalizeFeatureUsage(raw, fallbackNumeric);
  }, [tier, tierLimits, globalTierLimits, usageFeatures]);

  useEffect(() => {
    if (!setPageExtras) return undefined;
    if (!usageFeatures || !tier) {
      setPageExtras(null);
      return () => setPageExtras(null);
    }
    const features = {};
    for (const feature of USAGE_PAGE_FEATURES) {
      const row = getFeatureUsage(feature.apiKey, feature.legacyKey);
      features[feature.apiKey] = {
        used: row.used,
        limit: row.limit,
        percentage: row.percentage,
        is_unlimited: row.is_unlimited,
      };
    }
    setPageExtras({
      tier,
      ...(typeof resetInDays === 'number' ? { reset_in_days: resetInDays } : {}),
      features,
    });
    return () => setPageExtras(null);
  }, [setPageExtras, usageFeatures, tier, resetInDays, getFeatureUsage]);

  const handleBuyExtraPurchase = async (packageName) => {
    const purchaseResult = await purchaseTokenPackage(packageName);
    if (!purchaseResult.success) {
      return {
        status: false,
        message: purchaseResult.error || 'Purchase failed',
      };
    }

    const response = await buyExtraTokens(
      email,
      packageName,
      purchaseResult.transactionId,
    );
    if (response.status) {
      await loadUsage({ silent: true });
    }
    return response;
  };

  if (loading) {
    return <PageSkeleton shape="usage" />;
  }

  if (error) {
    return (
      <div className="w-full h-full overflow-x-hidden bg-[#F0F5F2] bg-dark-app">
        <div className="w-full pt-[24px] pb-[32px] px-[16px] md:px-[32px]">
          <p className="font-['Inter'] font-normal text-[16px] leading-[24px] text-[#828382] dark:text-[#828382]" style={{ fontSize: fontSize }}>
            {t('usage.errorLoading')}
          </p>
        </div>
      </div>
    );
  }

  const daysUntilReset = resetInDays ?? 0;
  const planName = formatTierName(tier);
  // "Elite plan: Resets in 20 days" — drops the plan prefix while the tier loads.
  const planSummary = planName
    ? `${t('usage.planName', { plan: planName })}: ${t('usage.resetsIn', { days: daysUntilReset })}`
    : t('usage.resetsIn', { days: daysUntilReset });

  return (
    <div className="w-full h-full overflow-y-auto bg-[#F0F5F2] bg-dark-app">
      <div className="w-full pt-[24px] pb-[32px] px-[16px] md:px-[32px]">
        {/* Header: title + plan summary on the left, Manage subscription on the right */}
        <div className="flex flex-col gap-[16px] sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-[2px] min-w-0">
            <h1 className="font-['Inter'] font-semibold text-[18px] leading-[27px] text-[#282A29] dark:text-[#f0f5f2]" style={{ fontSize: fontSize + 4 }}>
              {t('usage.headerTitle')}
            </h1>
            <p className="font-['Inter'] font-normal text-[14px] leading-[21px] text-[#5D625F] dark:text-[#828382]" style={{ fontSize }}>
              {planSummary}
            </p>
          </div>

          <button
            type="button"
            onClick={() => navigate(ROUTES.SUBSCRIPTION)}
            className="self-start shrink-0 bg-bloom-green hover:bg-bloom-green-hover rounded-[8px] px-[16px] py-[8px] font-['Inter'] font-medium text-white transition-colors whitespace-nowrap"
            style={{ fontSize }}
          >
            {t('billing.manageSubscription')}
          </button>
        </div>

        {/* Divider closing the header, 24px above and 32px below */}
        <div className="mt-[24px] mb-[32px] h-[1px] bg-[#E2E2E2] dark:bg-[#282a29]" />

        {/* Usage card */}
        <div className={`${CARD_CLASS} px-4 py-[24px] sm:px-8 lg:px-12`}>
          {USAGE_PAGE_FEATURES.map((feature, index) => {
            const isLast = index === USAGE_PAGE_FEATURES.length - 1;
            const featureUsage = getFeatureUsage(feature.apiKey, feature.legacyKey);
            const display = getUsageDisplay(featureUsage);
            const usedLabel = `${display.percentage}% ${t('usage.used')}`;
            const limitLabel = featureUsage.is_unlimited || featureUsage.limit === -1
              ? t('usage.unlimited')
              : featureUsage.limit === 0
                ? t('usage.notAvailable')
                : `${featureUsage.used.toLocaleString()}/${featureUsage.limit.toLocaleString()}`;

            return (
              <React.Fragment key={feature.apiKey}>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-6">
                  <div className="w-full sm:w-[160px] flex-shrink-0 min-w-0">
                    <span className="font-['Inter'] font-medium text-[16px] leading-[24px] text-[#191919] dark:text-[#f0f5f2]" style={{ fontSize: fontSize + 2 }}>
                      {t(feature.nameKey)}
                    </span>
                  </div>

                  <div className="flex-1 min-w-0 flex flex-col gap-[8px]">
                    <div className="h-[8px] w-full bg-[#E8EBE9] dark:bg-[#282a29] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-bloom-green rounded-full transition-all duration-300"
                        style={{ width: `${display.barFillPercent}%` }}
                      />
                    </div>

                    <div className="flex items-center justify-between gap-[12px]">
                      <span className="font-['Inter'] font-normal text-[14px] leading-[21px] text-[#5D625F] dark:text-[#828382] whitespace-nowrap" style={{ fontSize: fontSize - 2 }}>
                        {usedLabel}
                      </span>
                      <span className="font-['Inter'] font-normal text-[14px] leading-[21px] text-[#5D625F] dark:text-[#828382] whitespace-nowrap" style={{ fontSize: fontSize - 2 }}>
                        {limitLabel}
                      </span>
                    </div>
                  </div>
                </div>

                {!isLast && (
                  hasSeparatorAfter(index)
                    ? <div className="my-[24px] h-[1px] bg-[#E8EBE9] dark:bg-[#282a29]" />
                    : <div className="h-[32px]" />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Extend your limits card */}
        <div className={`${CARD_CLASS} mt-[32px] px-[48px] py-[20px] flex flex-col gap-[16px] sm:flex-row sm:items-center sm:justify-between`}>
          <span className="font-['Inter'] font-medium text-[16px] leading-[24px] text-[#191919] dark:text-[#f0f5f2]" style={{ fontSize: fontSize + 2 }}>
            {t('usage.extendYourLimits')}
          </span>

          <div className="flex flex-wrap items-center gap-[12px]">
            <span className="font-['Inter'] font-normal text-[14px] leading-[21px] text-[#5D625F] dark:text-[#828382]" style={{ fontSize: fontSize - 2 }}>
              {t('usage.buyExtra')}
            </span>
            <button
              type="button"
              onClick={() => setBuyExtraModalType(EXTRA_PACKAGES.BIQ)}
              className="font-['Inter'] font-medium text-bloom-green border border-bloom-green rounded-[8px] px-[16px] py-[8px] hover:bg-bloom-green/10 transition-colors whitespace-nowrap"
              style={{ fontSize }}
            >
              {t('usage.biqUsage')}
            </button>
            <button
              type="button"
              onClick={() => setBuyExtraModalType(EXTRA_PACKAGES.DEEP_THINKING)}
              className="font-['Inter'] font-medium text-bloom-green border border-bloom-green rounded-[8px] px-[16px] py-[8px] hover:bg-bloom-green/10 transition-colors whitespace-nowrap"
              style={{ fontSize }}
            >
              {t('usage.deepThinkingUsage')}
            </button>
          </div>
        </div>
      </div>

      <BuyExtraPackageModal
        open={!!buyExtraModalType}
        onClose={() => setBuyExtraModalType(null)}
        type={buyExtraModalType}
        packages={packages}
        onPurchase={handleBuyExtraPurchase}
      />
    </div>
  );
};

export default Usage;
